<template>
  <Menu id="" class="intro-x">
    <Menu.Button
      class="relative outline-none block select-none"
      :class="{
        'text-slate-600': layout === 'rubick' || layout === 'tinker',
        'text-white/70': layout === 'enigma' || layout === 'icewall',
      }"
    >
      <Lucide icon="Layout" class="notification__icon dark:text-slate-500" />
    </Menu.Button>
    <Menu.Items class="w-[250px] sm:w-[400px] mt-1.5" :placement=calculatePlacement()>
      <Menu.Header class="font-normal"> {{ $t(themeSettingsId) }} </Menu.Header>
      <Menu.Divider class="bg-slate-800/[0.08]" />
      <div class="grid grid-cols-12 gap-2 p-2">
        <div class="col-span-12 mb-2">{{ $t(layoutId) }}</div>
        <div
          v-for="(layout, index) in layouts"
          :key="index"
          class="col-span-6 sm:col-span-3 flex items-center p-2 flex justify-center rounded-md select-none"
          :class="[
            {
              'bg-primary text-white': layout === theme.layout,
              'bg-slate-50 dark:bg-white/10 hover:bg-slate-100 hover:dark:bg-white/20 cursor-pointer':
                layout !== theme.layout,
            },
          ]"
          @click="setTheme('layout', layout)"
          tabindex="0"
        >
          {{ layout.charAt(0).toUpperCase() + layout.slice(1) }}
        </div>
        <div class="col-span-12 mt-2 mb-2">{{ $t(menuPositionsId) }}</div>
        <div
          v-for="(menuPosition, index) in menuPositions"
          :key="index"
          class="col-span-6 sm:col-span-3 flex items-center p-2 flex justify-center rounded-md select-none"
          :class="[
            {
              'bg-primary text-white': menuPosition === theme.menuPosition,
              'bg-slate-50 dark:bg-white/10 hover:bg-slate-100 hover:dark:bg-white/20 cursor-pointer':
                menuPosition !== theme.menuPosition,
            },
          ]"
          @click="setTheme('menuPosition', menuPosition)"
          tabindex="0"
        >
          {{ menuPosition.charAt(0).toUpperCase() + menuPosition.slice(1) }}
        </div>
        <div class="col-span-12 mt-2">{{ $t(colorSchemeId) }}</div>
        <div class="col-span-12">
          <div class="flex flex-row">
            <div
              v-for="(colorScheme, index) in colorSchemes"
              :key="index"
              :class="[
                'block w-8 h-8 cursor-pointer rounded-full border-4 mr-1 hover:border-slate-200',
                colorScheme.color,
                {
                  'border-slate-300 dark:border-darkmode-800/80':
                    theme.colorScheme === colorScheme.id,
                  'border-white dark:border-darkmode-600':
                    theme.colorScheme !== colorScheme.id,
                },
              ]"
              @click="setTheme('colorScheme', colorScheme.id)"
            ></div>
          </div>
        </div>
        <div class="col-span-12 mt-2">{{ $t(darkModeId) }}</div>
        <div class="col-span-12">
          <div
            :class="[
              'border w-[38px] h-[24px] p-px outline-none rounded-full relative cursor-pointer',
              'before:content-[\'\'] before:w-[22px] before:h-[22px] before:transition-all before:duration-200 before:shadow-[1px_1px_3px_rgba(0,0,0,0.25)] before:absolute before:inset-y-0 before:my-auto before:rounded-full',
              {
                'bg-primary border-primary': theme.darkMode,
                'before:ml-[13px] before:bg-white': theme.darkMode,
              },
            ]"
            @click="setTheme('darkMode', !theme.darkMode)"
          ></div>
        </div>
      </div>
    </Menu.Items>
  </Menu>
</template>

<script setup lang="ts">
import { onMounted, ref, defineProps } from "vue";
import Menu from "@/base-components/Headless/Menu";
import Lucide from "@/base-components/Lucide/Lucide.vue";
import {
  ColorScheme,
  ITheme,
  Layout,
  MenuPosition,
} from "@/ts/interfaces/theme.interface";
import { loadMessages } from '../../services/translation/translation.service';

const layouts: Layout[] = ["rubick", "enigma", "icewall", "tinker"];
const menuPositions: MenuPosition[] = ["side", "simple", "top"];
const colorSchemes: { id: ColorScheme; color: string }[] = [
  {
    id: "blue",
    color: "bg-blue-800",
  },
  {
    id: "emerald",
    color: "bg-emerald-900",
  },
  {
    id: "indigo",
    color: "bg-blue-900",
  },
  {
    id: "cyan",
    color: "bg-cyan-900",
  },
  {
    id: "violet",
    color: "bg-indigo-900",
  },
  {
    id: "red",
    color: "bg-red-900",
  },
   {
    id: "orange",
    color: "bg-orange-600",
  },
];

const theme = ref();

const defaultTheme = {
  layout: "rubick",
  menuPosition: "side",
  colorScheme: "blue",
  darkMode: false,
};

//set theme
if (!localStorage.getItem("theme")) {
  localStorage.setItem("theme", JSON.stringify(defaultTheme));
}

const props = defineProps({
  layout: String,
});

const themeSettingsId = '3127888342503391838';
const layoutId = '3127218632900215137';
const menuPositionsId = '3127888342503391848';
const colorSchemeId = '3127888342503391855';
const darkModeId = '3127888342503391863';

onMounted(async () => {
  const themeFromLocalstorage = localStorage.getItem("theme");

  if (!themeFromLocalstorage) {
    theme.value = defaultTheme;
  }

  if (themeFromLocalstorage) {
    theme.value = JSON.parse(themeFromLocalstorage);
  }

  setColorScheme();
  setDarkMode();

  const data = [
    themeSettingsId,
    layoutId,
    menuPositionsId,
    colorSchemeId,
    darkModeId
  ];

  await loadMessages(data);
});

function calculatePlacement() {
  if (window.innerWidth > 640) {
    return "bottom-end"
  } else {
    "bottom-start"
  }
}

function setTheme(key: string, value: string) {
  theme.value[key] = value;
  localStorage.setItem("theme", JSON.stringify(theme.value));

  if (key === "colorScheme") {
    setColorScheme();
  }

  if (key === "darkMode") {
    setDarkMode();
  }

  const updateSubApp = ["colorScheme", "darkMode"].includes(key);

  window.dispatchEvent(
    new CustomEvent("updateTheme", { detail: { updateSubApp: updateSubApp } })
  );
}

function setColorScheme() {
  const body = document.body;

  if (!body) {
    return;
  }

  colorSchemes.forEach((scheme: any) => {
    if (body.classList.contains(scheme.id)) {
      body.classList.remove(scheme.id);
    }
  });

  body.classList.add(theme.value.colorScheme);
}

const setDarkMode = () => {
  const body = document.body;

  if (!body) {
    return;
  }

  if (theme.value.darkMode) {
    body.classList.add("dark");
  }

  if (!theme.value.darkMode) {
    body.classList.remove("dark");
  }
  window.dispatchEvent(
    new CustomEvent("updateTheme", { detail: { updateSubApp: true } })
  );
};
</script>
